/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { CutLengthsLine } from "@eisinc/App/Components/CutWire/CutWireModal";
import TextField, { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import { QTY_MAX_VALUE } from "@eisinc/XDL/constants";
import Close from "@eisinc/XDL/Icons/Close";
import Delete from "@eisinc/XDL/Icons/Delete";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

interface CutFieldsStyles {
    contentWrapper?: InjectableCss;
    tableHeading?: InjectableCss;
    fieldsWrapper?: InjectableCss;
    inputWrapper?: InjectableCss;
    inputFields?: TextFieldPresentationProps;
    totalColumn?: InjectableCss;
    removeBtn?: InjectableCss;
}

const styles: CutFieldsStyles = {
    contentWrapper: {
        css: css`
            padding: 0 50px 0 64px;
            @media (max-width: 481px) {
                padding: 0;
            }
        `,
    },
    tableHeading: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
            & span {
                width: calc(100% / 3);
                font-size: 14px;
                line-height: 18px;
                font-weight: bold;
            }

            & span:last-child {
                @media (max-width: 481px) {
                    padding-left: 12px;
                }
            }
        `,
    },
    fieldsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
        `,
    },
    totalColumn: {
        css: css`
            display: flex;
            align-items: center;
            width: calc(100% / 3);
            span {
                width: 70%;
            }
            @media (max-width: 481px) {
                padding-left: 12px;
            }
        `,
    },
    inputWrapper: {
        css: css`
            display: flex;
            align-items: center;
            width: calc(100% / 3);
        `,
    },
    inputFields: {
        cssOverrides: {
            formInputWrapper: css`
                margin: 0 !important;
            `,
            formField: css`
                max-width: 105px;
                input {
                    width: 90px !important;
                    margin-right: 10px;
                }
                @media (max-width: 481px) {
                    width: 99px;
                }
                @media (max-width: 350px) {
                    width: 93px;
                }
            `,
        },
    },
    removeBtn: {
        css: css`
            &:focus {
                svg {
                    color: black;
                }
            }
        `,
    },
};

interface TableProps {
    unitOfMeasureDescription: string;
    cutObject: CutLengthsLine[];
    lineId: string;
    setCutObject: any;
    cartLine?: CartLineModel;
}

const CutWireFieldsTable: FC<TableProps> = ({
    unitOfMeasureDescription,
    cutObject,
    lineId,
    setCutObject,
    cartLine,
}) => {
    return (
        <StyledWrapper {...styles.contentWrapper}>
            <StyledWrapper {...styles.tableHeading}>
                <Typography as="span">{translate("Cuts")}</Typography>
                <Typography as="span">{unitOfMeasureDescription}</Typography>
                <Typography as="span">{translate("Total")}</Typography>
            </StyledWrapper>
            {cutObject.length > 0 &&
                cutObject.map((item: CutLengthsLine, index: number) => (
                    <CutWireFields
                        key={item.uid}
                        lineId={lineId}
                        id={index}
                        cutLine={item}
                        cutObject={cutObject}
                        setCutObject={setCutObject}
                        data-test-select={`cut_row_${index}`}
                        cartLine={cartLine}
                    />
                ))}
        </StyledWrapper>
    );
};

interface CutFieldsRowProps {
    cutObject: CutLengthsLine[];
    setCutObject: any;
    cutLine: CutLengthsLine;
    id: number;
    lineId: string;
    cartLine?: CartLineModel;
}

const CutWireFields: FC<CutFieldsRowProps> = ({ cutObject, setCutObject, cutLine, id, lineId, cartLine }) => {
    const [cutCount, setCutCount] = useState(cutLine.cutCount.toString());
    const [cutValue, setCutValue] = useState(cutLine.cutValue.toString());
    const [allowFractionalQty, setAllowFractionalQty] = React.useState(false);

    const regexp = allowFractionalQty ? /^[0-9]*[.,]?[0-9]*$/ : /^[0-9\b]+$/;

    const isValidNumberic = (number: string) => {
        if (number === "") {
            // backspace or delete case
            return true;
        }
        return regexp.test(number);
    };

    React.useEffect(() => {
        setCutCount(cutLine.cutCount?.toString());
    }, [cutLine.cutCount]);

    React.useEffect(() => {
        setCutValue(cutLine.cutValue?.toString());
    }, [cutLine.cutValue]);

    React.useEffect(() => {
        const isFractionalMoq = cartLine?.properties?.isFractionalMoq?.toLowerCase() === "true";
        setAllowFractionalQty(isFractionalMoq);
    }, [cartLine]);

    const cutCountChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
        if (!isValidNumberic(event.currentTarget.value)) {
            event.preventDefault();
            return;
        }
        const value = event.currentTarget.value;
        setCutCount(value);
    };

    const cutCountKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            event.stopPropagation();
            (event.target as any).blur();
        }
    };

    const cutCountBlurHanlder = (event: React.FocusEvent<HTMLInputElement>, id: number) => {
        const stringValue = event.target.value;
        const value = Number.parseInt(stringValue, 10);
        if (!Number.isNaN(value) && value >= 0 && value <= QTY_MAX_VALUE) {
            if (cutObject[id] !== null) {
                const total = (parseInt(cutValue, 10) * parseInt(stringValue, 10)).toString();
                const newCutLine: CutLengthsLine = {
                    cutCount: stringValue,
                    cutValue,
                    total,
                    uid: cutLine.uid,
                };
                const newCutObject = [...cutObject];
                newCutObject[id] = newCutLine;
                setCutObject(newCutObject);
            }
            if (cutObject[id] === null) {
                const newCutLine: CutLengthsLine = {
                    cutCount: stringValue,
                    cutValue: "0",
                    total: "0",
                    uid: uniqueId(),
                };
                const newCutObject = [...cutObject];
                newCutObject[id] = newCutLine;
                setCutObject(newCutObject);
            }
        }
        if (value < 0 || value > QTY_MAX_VALUE) {
            setCutCount(cutLine.cutCount?.toString());
        }
    };

    const cutValueChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
        if (!isValidNumberic(event.currentTarget.value)) {
            event.preventDefault();
            return;
        }
        const value = event.currentTarget.value;
        setCutValue(value);
    };

    const cutValueKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            event.stopPropagation();
            (event.target as any).blur();
        }
    };

    const cutValueBlurHandler = (event: React.FocusEvent<HTMLInputElement>, id: number) => {
        const stringValue = event.target.value;
        const value = Number.parseInt(stringValue, 10);
        if (!Number.isNaN(value) && value >= 0 && value <= QTY_MAX_VALUE) {
            if (cutObject[id] !== null) {
                const total = (parseInt(cutCount, 10) * parseInt(stringValue, 10)).toString();
                const newCutLine: CutLengthsLine = {
                    cutCount,
                    cutValue: stringValue,
                    total,
                    uid: cutLine.uid,
                };
                const newCutObject = [...cutObject];
                newCutObject[id] = newCutLine;
                setCutObject(newCutObject);
            }
            if (cutObject[id] === null) {
                const newCutLine: CutLengthsLine = {
                    cutCount: "0",
                    cutValue: stringValue,
                    total: "0",
                    uid: uniqueId(),
                };
                const newCutObject = [...cutObject];
                newCutObject[id] = newCutLine;
                setCutObject(newCutObject);
            }
        }
        if (value < 0 || value > QTY_MAX_VALUE) {
            setCutValue(cutLine.cutValue?.toString());
        }
    };

    const deleteCutRow = (e: any, id: number) => {
        // more actions here to handle total and qty changes
        const newCutObject = [...cutObject];
        newCutObject.splice(id, 1);
        setCutObject(newCutObject);
    };

    return (
        <StyledWrapper {...styles.fieldsWrapper}>
            <StyledWrapper {...styles.inputWrapper}>
                <TextField
                    {...styles.inputFields}
                    type="number"
                    value={cutCount}
                    min={0}
                    onChange={(e: any) => cutCountChangeHandler(e, id)}
                    onKeyDown={cutCountKeyDownHandler}
                    onBlur={e => cutCountBlurHanlder(e, id)}
                    data-test-selector={`cut_count_${lineId}`}
                    tabIndex={id + 1}
                    autoFocus={id === 0}
                />
                <Typography as="span">X</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.inputWrapper}>
                <TextField
                    {...styles.inputFields}
                    type="number"
                    value={cutValue}
                    min={0}
                    onChange={(e: any) => cutValueChangeHandler(e, id)}
                    onKeyDown={cutValueKeyDownHandler}
                    onBlur={e => cutValueBlurHandler(e, id)}
                    data-test-selector={`cut_value_${lineId}`}
                    tabIndex={id + 1}
                />
                <Typography as="span">=</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.totalColumn}>
                <Typography as="span">{cutLine.total}</Typography>
                <Clickable onClick={(e: any) => deleteCutRow(e, id)} tabIndex={id + 1} {...styles.removeBtn}>
                    <Icon src={Delete} size={16} color="primary.main" />
                </Clickable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default CutWireFieldsTable;
